import {EventForm} from '../../forms/EventForm';
import {InsertEventDto} from '../../../generated/models/InsertEventDto';
import {InsertBulkEventDto} from '../../../generated/models/InsertBulkEventDto';
import {apiRequest} from '../../../common/services/api';
import {insertEventEndpoint, insertBulkEventEndpoint} from '../../../common/constants/endpoints';
import {useOrganizerStore} from '../../../common/stores/useOrganizerStore';
import {useEffect, useState} from 'react';
import {useUiStore} from '../../../common/stores/useUiStore';
import {useLocation, useNavigate} from 'react-router-dom';
import routes from '../../routes/routes';
import {useTagsStore} from '../../../common/stores/useTagsStore';
import {FormFillerButton, FillPayload} from '../../button/FormFillerButton';
import {parseApiError} from '../../../utils/apiError';
import {MessageCard} from '../../card/messageCard';

export default function CreateEventScreen() {
  const {organizer} = useOrganizerStore();
  const navigate = useNavigate();
  const location = useLocation();
  const {setIsLoading, setShowBackButton} = useUiStore(state => state);
  const {clearSelectedTagCodes, selectedTagCodes} = useTagsStore();
  const [fillPayload, setFillPayload] = useState<FillPayload | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowBackButton(true);
    return () => {
      setShowBackButton(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateEvent = async (data: InsertEventDto[]) => {
    setApiError(null);
    const isRecurrent = data.length > 1;

    if (isRecurrent) {
      const base = data[0];
      const bulkBody: InsertBulkEventDto = {
        name: base.name,
        description: base.description,
        locationCode: base.locationCode,
        coordinates: base.coordinates,
        address: base.address,
        tagCodes: selectedTagCodes ?? [],
        mapImageCdnPaths: base.mapImageCdnPaths,
        occurrences: data.map(item => ({
          startDateTime: item.startDateTime,
          endDateTime: item.endDateTime,
        })),
      };

      const response = await apiRequest<string, InsertBulkEventDto>(
        {
          endpoint: insertBulkEventEndpoint(organizer?.id || ''),
          method: 'POST',
          body: {
            ...bulkBody,
            occurrences: bulkBody.occurrences.map(occ => ({
              ...occ,
              startDateTime: new Date(occ.startDateTime).toISOString(),
              endDateTime: new Date(occ.endDateTime).toISOString(),
            })),
          } as any,
        },
        {onError: err => setApiError(parseApiError(err) ?? 'Could not create bulk event. Something went wrong')},
      );

      if (!response?.data) {
        setIsLoading(false);
        return;
      }
    } else {
      const item = data[0];
      const response = await apiRequest<string, InsertEventDto>(
        {
          endpoint: insertEventEndpoint(organizer?.id || ''),
          method: 'POST',
          body: {
            ...item,
            startDateTime: item.startDateTime.toISOString(),
            endDateTime: item.endDateTime.toISOString(),
            tagCodes: selectedTagCodes,
          } as any,
        },
        {onError: err => setApiError(parseApiError(err) ?? 'Could not create event. Something went wrong')},
      );

      if (!response?.data) {
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
    clearSelectedTagCodes();
    // Started from All Activities: go back there.
    navigate(location.state?.returnTo ?? routes.getAllEvents);
  };

  return (
    <div
      style={{
        maxWidth: 500,
        margin: '40px auto', // Centered and spaced from header
        padding: '40px',
        backgroundColor: '#353535', // Dashboard background
        borderRadius: '16px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      }}>
      <h2 style={{color: '#fff', marginBottom: '30px', textAlign: 'center'}}>
        Create New Event
      </h2>
      <EventForm
        type="CREATE"
        onSubmit={handleCreateEvent}
        fillPayload={fillPayload}
      />
      <MessageCard type={apiError ? 'error' : null} message={apiError} />
      <FormFillerButton onFill={setFillPayload} />
    </div>
  );
}
